<template>
  <div class="intervenant-item">
    <!-- Intervenant Header -->
    <div class="intervenant-header">
      <div class="avatar-column">
        <Avatar 
          :label="getInitials(intervenant.intervenantName)"
          shape="circle"
          size="large"
          class="intervenant-avatar"
          :style="{ backgroundColor: getIntervenantColor(intervenant.intervenantId) }"
        />
      </div>
      <div class="info-column">
        <h3 class="intervenant-name">{{ intervenant.intervenantName }}</h3>
        <div class="contact-info" v-if="intervenant.email || intervenant.phone">
          <div v-if="intervenant.email" class="contact-item">
            <i class="pi pi-envelope"></i>
            <span>{{ intervenant.email }}</span>
          </div>
          <div v-if="intervenant.phone" class="contact-item">
            <i class="pi pi-phone"></i>
            <span>{{ intervenant.phone }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Budget Overview -->
    <div class="budget-overview">
      <!-- Budget Metrics -->
      <div class="budget-metrics">
        <div class="metric budget-metric">
          <div class="metric-icon budget">
            <i class="pi pi-wallet"></i>
          </div>
          <div class="metric-content">
            <span class="metric-label">{{ $t('reports.totalBudget') }}</span>
            <span class="metric-value budget">{{ formattedBudget || '...' }}</span>
          </div>
        </div>
        
        <div class="metric consumed-metric">
          <div class="metric-icon consumed">
            <i class="pi pi-chart-pie"></i>
          </div>
          <div class="metric-content">
            <span class="metric-label">{{ $t('reports.consumed') }}</span>
            <span class="metric-value consumed">{{ formattedConsumed || '...' }}</span>
          </div>
        </div>
        
        <div class="metric remaining-metric">
          <div class="metric-icon remaining" :class="{ 'negative': intervenant.totalRemainingAmount < 0 }">
            <i class="pi pi-chart-line"></i>
          </div>
          <div class="metric-content">
            <span class="metric-label">{{ $t('reports.remaining') }}</span>
            <span 
              class="metric-value remaining"
              :class="{ 'negative': intervenant.totalRemainingAmount < 0 }"
            >
              {{ formattedRemaining || '...' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Progress and Timeline Row -->
      <div class="progress-timeline-row">
        <!-- Progress Bar -->
        <div class="consumption-progress">
          <div class="progress-header">
            <span class="progress-label">{{ $t('reports.consumption.percentage') }}</span>
            <span 
              class="progress-text"
              :class="getPercentageClass(intervenant.totalConsumptionPercentage)"
            >
              {{ formatPercentage(intervenant.totalConsumptionPercentage) }}
            </span>
          </div>
          <ProgressBar 
            :value="Math.min(intervenant.totalConsumptionPercentage || 0, 100)"
            :class="getProgressBarClass(intervenant.totalConsumptionPercentage)"
            :style="getProgressBarStyle(intervenant.totalConsumptionPercentage)"
            class="modern-progress"
          />
        </div>

        <!-- Expense Timeline -->
        <div v-if="intervenant.expenseTransactions && intervenant.expenseTransactions.length > 0" class="expense-timeline">
          <div class="timeline-header">
            <i class="pi pi-calendar"></i>
            <span>{{ $t('reports.expenseTimeline') }}</span>
            <small class="timeline-count">
              ({{ intervenant.expenseTransactions.length }} {{ $t('reports.transactions') }})
              <span v-if="getSampledTransactions(intervenant.expenseTransactions).sampled.length < intervenant.expenseTransactions.length" 
                    class="timeline-sample-info">
                - {{ $t('reports.showing') }} {{ getSampledTransactions(intervenant.expenseTransactions).sampled.length }}
              </span>
            </small>
          </div>
          
          <!-- Enhanced Timeline with Smart Sampling -->
          <div class="timeline-visual">
            <div class="timeline-track">
              <div class="timeline-line"></div>
              
              <!-- Sampled Transaction Dots (when collapsed) -->
              <template v-if="!isExpanded">
                <div 
                  v-for="(item, index) in getSampledTransactions(intervenant.expenseTransactions).sampled" 
                  :key="`transaction-${item.originalIndex}`"
                  class="timeline-dot-container" 
                  :style="getTimelineDotPosition(index, getSampledTransactions(intervenant.expenseTransactions).sampled.length)"
                >
                  <div 
                    class="timeline-dot" 
                    :class="getTimelineDotClass(index, getSampledTransactions(intervenant.expenseTransactions).sampled.length, item.isImportant)"
                    :title="formatTransactionTooltip(item.transaction)"
                  >
                    <div v-if="item.isImportant" class="dot-importance-indicator"></div>
                  </div>
                  <div class="dot-date-label">
                    {{ formatDateAmount(item.transaction) }}
                  </div>
                </div>
                
                <!-- Overflow indicator for hidden transactions -->
                <div 
                  v-if="getSampledTransactions(intervenant.expenseTransactions).hasMore"
                  class="timeline-overflow-indicator"
                  :title="$t('reports.moreTransactions', { count: getSampledTransactions(intervenant.expenseTransactions).hiddenCount })"
                  @click="showAllTransactions"
                >
                  <i class="pi pi-ellipsis-h"></i>
                  <span class="overflow-count">+{{ getSampledTransactions(intervenant.expenseTransactions).hiddenCount }}</span>
                </div>
              </template>
              
              <!-- All Transaction Dots (when expanded) -->
              <template v-else>
                <div 
                  v-for="(transaction, index) in intervenant.expenseTransactions" 
                  :key="`transaction-full-${index}`"
                  class="timeline-dot-container" 
                  :style="getTimelineDotPosition(index, intervenant.expenseTransactions.length)"
                >
                  <div 
                    class="timeline-dot" 
                    :class="getTimelineDotClass(index, intervenant.expenseTransactions.length, isLargestTransaction(transaction, intervenant.expenseTransactions))"
                    :title="formatTransactionTooltip(transaction)"
                  >
                    <div v-if="isLargestTransaction(transaction, intervenant.expenseTransactions)" class="dot-importance-indicator"></div>
                  </div>
                  <div class="dot-date-label">
                    {{ formatDateAmount(transaction) }}
                  </div>
                </div>
              </template>
            </div>
          </div>
          
          <!-- Show All Toggle for many transactions -->
          <div v-if="intervenant.expenseTransactions.length > 5" class="timeline-actions">
            <button 
              class="timeline-toggle-btn"
              @click="toggleExpanded"
              :class="{ active: isExpanded }"
            >
              <i :class="isExpanded ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
              {{ isExpanded ? $t('reports.showLess') : $t('reports.showAll') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- All Transactions Modal -->
    <Modal
      :show="showAllTransactionsModal"
      :title="$t('reports.allTransactions')"
      size="large"
      @close="closeAllTransactionsModal"
    >
      <div class="all-transactions-modal">
        <div class="modal-header-info">
          <h4>{{ intervenant.intervenantName }}</h4>
          <p class="transaction-count">
            {{ intervenant.expenseTransactions?.length || 0 }} {{ $t('reports.transactionCount') }}
          </p>
        </div>

        <div class="transactions-table">
          <table class="table">
            <thead>
              <tr>
                <th>{{ $t('reports.transactions.date') }}</th>
                <th>{{ $t('reports.transactions.amount') }}</th>
                <th>{{ $t('reports.transactions.description') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(transaction, index) in intervenant.expenseTransactions" 
                :key="transaction.id || index"
                :class="{ 'important-row': isLargestTransaction(transaction, intervenant.expenseTransactions) }"
              >
                <td>{{ formatDate(transaction.transactionDate) }}</td>
                <td class="amount-cell">
                  <span :class="{ 'largest-amount': isLargestTransaction(transaction, intervenant.expenseTransactions) }">
                    {{ formattedTransactions[transaction.id ? String(transaction.id) : `index-${index}`] || '...' }}
                  </span>
                </td>
                <td>{{ transaction.description || $t('reports.noDescription') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDate, formatPercentage } from '@/utils/formatters';
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils';
import ProgressBar from 'primevue/progressbar';
import Avatar from 'primevue/avatar';
import Modal from '@/components/base/Modal.vue';

interface Props {
  intervenant: any;
}

const props = defineProps<Props>();

const { t } = useI18n();

// Local state
const isExpanded = ref(false);
const showAllTransactionsModal = ref(false);
const formattedBudget = ref<string>('');
const formattedConsumed = ref<string>('');
const formattedRemaining = ref<string>('');
const formattedTransactions = ref<Record<string, string>>({});

// Methods
const getInitials = (name: string | undefined) => {
  if (!name) return '';
  return name
    .split(' ')
    .slice(0, 2)
    .map((word: string) => word.charAt(0).toUpperCase())
    .join('');
};

const getIntervenantColor = (intervenantId: number) => {
  const colors = [
    '#667eea', '#764ba2', '#f093fb', '#f5576c',
    '#4facfe', '#00f2fe', '#43e97b', '#38f9d7',
    '#ffecd2', '#fcb69f', '#a8edea', '#fed6e3',
    '#ff9a9e', '#fecfef', '#ffecd2', '#fcb69f'
  ];
  return colors[intervenantId % colors.length];
};

const getProgressBarClass = (percentage: number | undefined) => {
  return 'progress-dynamic';
};

const getProgressBarStyle = (percentage: number | undefined) => {
  const normalizedPercentage = Math.min(Math.max(percentage || 0, 0), 100);
  
  let backgroundColor;
  if (normalizedPercentage === 0) {
    backgroundColor = '#ff4757';
  } else if (normalizedPercentage <= 20) {
    backgroundColor = '#ff6b35';
  } else if (normalizedPercentage <= 40) {
    backgroundColor = '#ffa502';
  } else if (normalizedPercentage <= 60) {
    backgroundColor = '#ffdd59';
  } else if (normalizedPercentage <= 80) {
    backgroundColor = '#7bed9f';
  } else if (normalizedPercentage < 100) {
    backgroundColor = '#2ed573';
  } else {
    backgroundColor = '#2ed573';
  }
  
  return {
    '--progress-color': backgroundColor
  };
};

const getPercentageClass = (percentage: number | undefined) => {
  const normalizedPercentage = Math.min(Math.max(percentage || 0, 0), 100);
  
  if (normalizedPercentage >= 80) return 'high-usage';
  if (normalizedPercentage >= 60) return 'medium-usage'; 
  if (normalizedPercentage >= 40) return 'medium-low-usage';
  if (normalizedPercentage >= 20) return 'low-medium-usage';
  return 'low-usage';
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const showAllTransactions = () => {
  showAllTransactionsModal.value = true;
};

const closeAllTransactionsModal = () => {
  showAllTransactionsModal.value = false;
};

const getSampledTransactions = (transactions: any[] | undefined) => {
  if (!transactions || transactions.length === 0) {
    return { sampled: [], hasMore: false, hiddenCount: 0 };
  }

  const MAX_VISIBLE_TRANSACTIONS = 5;
  
  if (transactions.length <= MAX_VISIBLE_TRANSACTIONS) {
    return {
      sampled: transactions.map((transaction, index) => ({
        transaction,
        originalIndex: index,
        isImportant: false
      })),
      hasMore: false,
      hiddenCount: 0
    };
  }

  const sortedByAmount = [...transactions]
    .map((transaction: any, index: number) => ({ transaction, originalIndex: index }))
    .sort((a: any, b: any) => (b.transaction.amount || 0) - (a.transaction.amount || 0));

  const sortedByDate = [...transactions]
    .map((transaction: any, index: number) => ({ transaction, originalIndex: index }))
    .sort((a: any, b: any) => new Date(b.transaction.transactionDate).getTime() - new Date(a.transaction.transactionDate).getTime());

  const mustInclude = new Set();
  
  mustInclude.add(sortedByDate[0].originalIndex);
  mustInclude.add(sortedByDate[sortedByDate.length - 1].originalIndex);
  mustInclude.add(sortedByAmount[0].originalIndex);
  
  if (MAX_VISIBLE_TRANSACTIONS > 3) {
    if (sortedByAmount.length > 1) {
      mustInclude.add(sortedByAmount[1].originalIndex);
    }
    
    const middleIndex = Math.floor(sortedByDate.length / 2);
    mustInclude.add(sortedByDate[middleIndex].originalIndex);
  }

  const selectedIndices = Array.from(mustInclude).slice(0, MAX_VISIBLE_TRANSACTIONS) as number[];

  const sampled = selectedIndices
    .sort((a: number, b: number) => a - b)
    .map((originalIndex: number) => ({
      transaction: transactions[originalIndex],
      originalIndex,
      isImportant: originalIndex === sortedByAmount[0].originalIndex
    }));

  return {
    sampled,
    hasMore: transactions.length > MAX_VISIBLE_TRANSACTIONS,
    hiddenCount: transactions.length - sampled.length
  };
};

const isLargestTransaction = (transaction: any, allTransactions: any[]) => {
  if (!allTransactions || allTransactions.length === 0) return false;
  const maxAmount = Math.max(...allTransactions.map((t: any) => t.amount || 0));
  return (transaction.amount || 0) === maxAmount;
};

const getTimelineDotPosition = (index: number, totalDots: number) => {
  if (totalDots === 1) {
    return { left: '50%' };
  }
  const percentage = (index / (totalDots - 1)) * 100;
  return { 
    left: `${Math.max(0, Math.min(100, percentage))}%`,
    transform: 'translateX(-50%)'
  };
};

const getTimelineDotClass = (index: number, totalDots: number, isImportant = false) => {
  const classes = ['timeline-dot'];
  
  if (isImportant) classes.push('important-transaction');
  
  if (index === 0) classes.push('first-dot');
  if (index === totalDots - 1) classes.push('last-dot');
  if (index === Math.floor(totalDots / 2)) classes.push('middle-dot');
  
  if (totalDots > 10) classes.push('dense-timeline');
  if (totalDots <= 3) classes.push('sparse-timeline');
  
  return classes.join(' ');
};

const formatDateAmount = (transaction: any) => {
    const date = formatShortDate(transaction.transactionDate);
  // Try to get formatted amount by id first, then by index
  let amount = '...';
  if (transaction.id && formattedTransactions.value[String(transaction.id)]) {
    amount = formattedTransactions.value[String(transaction.id)];
  } else {
    // Find transaction by reference in the array
    const transactions = props.intervenant.expenseTransactions || [];
    const index = transactions.findIndex((t: any) => t === transaction);
    if (index !== -1) {
      amount = formattedTransactions.value[`index-${index}`] || 
               formattedTransactions.value[String(transaction.id)] || '...';
    }
  }
  return `${date} - ${amount}`;
};

const formatTransactionTooltip = (transaction: any) => {
  const date = formatDate(transaction.transactionDate);
  // Try to get formatted amount by id first, then by index
  let amount = '...';
  if (transaction.id && formattedTransactions.value[String(transaction.id)]) {
    amount = formattedTransactions.value[String(transaction.id)];
  } else {
    // Find transaction by reference in the array
    const transactions = props.intervenant.expenseTransactions || [];
    const index = transactions.findIndex((t: any) => t === transaction);
    if (index !== -1) {
      amount = formattedTransactions.value[`index-${index}`] || 
               formattedTransactions.value[String(transaction.id)] || '...';
    }
  }
  const description = transaction.description || t('reports.noDescription');
  return `${date}\nAmount: ${amount}\nDescription: ${description}`;
};

const formatShortDate = (date: string | undefined) => {
  if (!date) return '';
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString(undefined, { 
    month: 'short', 
    day: 'numeric' 
  });
};

const formatAmounts = async () => {
  try {
    formattedBudget.value = await formatCurrencyWithUserPreference(props.intervenant.totalBudgetAllocated);
    formattedConsumed.value = await formatCurrencyWithUserPreference(props.intervenant.totalConsumedAmount);
    formattedRemaining.value = await formatCurrencyWithUserPreference(props.intervenant.totalRemainingAmount);
    
    if (props.intervenant.expenseTransactions?.length) {
      const transactionAmounts: Record<string, string> = {};
      for (let i = 0; i < props.intervenant.expenseTransactions.length; i++) {
        const transaction = props.intervenant.expenseTransactions[i];
        // Use both transaction.id and index as fallback
        const key = transaction.id ? String(transaction.id) : `index-${i}`;
        transactionAmounts[key] = await formatCurrencyWithUserPreference(transaction.amount);
      }
      formattedTransactions.value = transactionAmounts;
    }
  } catch (err) {
    console.warn('Error formatting currency:', err);
    formattedBudget.value = props.intervenant.totalBudgetAllocated?.toString() || '0';
    formattedConsumed.value = props.intervenant.totalConsumedAmount?.toString() || '0';
    formattedRemaining.value = props.intervenant.totalRemainingAmount?.toString() || '0';
  }
};

watch(() => props.intervenant, formatAmounts, { immediate: true });
</script>

<style scoped>
.intervenant-item {
  border: 1px solid var(--neutral-200);
  border-radius: 16px;
  padding: var(--space-5);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.intervenant-item:hover {
  border-color: var(--primary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.intervenant-header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
  padding-bottom: var(--space-4);
  border-bottom: 2px solid var(--neutral-100);
}

.avatar-column {
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.info-column {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1;
  min-width: 0;
}

.intervenant-avatar {
  width: 56px;
  height: 56px;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-lg);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.intervenant-avatar:hover {
  transform: scale(1.05);
}

.intervenant-name {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--neutral-900);
  letter-spacing: -0.02em;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.contact-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--neutral-700);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background: rgba(102, 126, 234, 0.05);
  transition: all 0.2s ease;
}

.contact-item:hover {
  background: rgba(102, 126, 234, 0.1);
  transform: translateX(4px);
}

.contact-item i {
  color: var(--primary);
  width: 16px;
  font-size: var(--font-size-sm);
  flex-shrink: 0;
}

.contact-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.budget-overview {
  margin-bottom: var(--space-4);
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-3);
}

.budget-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-3);
}

.metric {
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: var(--space-4);
  text-align: left;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.metric:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  border-color: var(--primary);
}

.metric-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 var(--space-3) 0;
  font-size: var(--font-size-base);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.metric:hover .metric-icon {
  transform: scale(1.1) rotate(5deg);
}

.metric-icon.budget {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.metric-icon.consumed {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.metric-icon.remaining {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.metric-icon.remaining.negative {
  background: linear-gradient(135deg, #ff4757 0%, #ff6b35 100%);
}

.metric-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.metric-label {
  font-size: var(--font-size-xs);
  color: var(--neutral-600);
  text-transform: uppercase;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.05em;
}

.metric-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--neutral-900);
  margin-top: var(--space-1);
}

.metric-value.negative {
  color: var(--error);
}

.consumption-progress {
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: var(--space-4);
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-2);
}

.progress-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--neutral-700);
}

.progress-text {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-base);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  background: rgba(0, 0, 0, 0.05);
}

.progress-text.high-usage {
  color: var(--success);
  background: rgba(46, 213, 115, 0.1);
}

.progress-text.medium-usage {
  color: var(--info);
  background: rgba(74, 144, 226, 0.1);
}

.progress-text.medium-low-usage {
  color: var(--warning);
  background: rgba(255, 165, 2, 0.1);
}

.progress-text.low-medium-usage,
.progress-text.low-usage {
  color: var(--error);
  background: rgba(255, 71, 87, 0.1);
}

.modern-progress {
  background: var(--neutral-200);
  border-radius: 8px;
  height: 16px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.modern-progress .p-progressbar-value) {
  border-radius: 8px;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(90deg, var(--progress-color, var(--primary)), color-mix(in srgb, var(--progress-color, var(--primary)) 80%, white));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  position: relative;
}

:deep(.modern-progress .p-progressbar-value::after) {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
  border-radius: 8px;
}

.expense-timeline {
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: var(--space-4);
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.timeline-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--neutral-800);
}

.timeline-header i {
  color: var(--primary);
  font-size: var(--font-size-base);
}

.timeline-count {
  color: var(--neutral-500);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-normal);
}

.timeline-visual {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.timeline-track {
  position: relative;
  height: 40px;
  padding: var(--space-3) 0;
}

.timeline-line {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 70%, white) 100%);
  border-radius: 2px;
  transform: translateY(-50%);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.timeline-dot {
  position: relative;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--primary);
  border: 3px solid white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.timeline-dot:hover {
  transform: scale(1.4);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  border-width: 4px;
}

.timeline-dot.important-transaction {
  background: linear-gradient(135deg, #ff4757 0%, #ff6b35 100%);
  width: 14px;
  height: 14px;
  border: 4px solid white;
  box-shadow: 0 4px 12px rgba(245, 87, 108, 0.5);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(245, 87, 108, 0.5);
  }
  50% {
    box-shadow: 0 4px 20px rgba(245, 87, 108, 0.8);
  }
}

.timeline-dot.important-transaction:hover {
  transform: scale(1.5);
}

.dot-importance-indicator {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 6px;
  height: 6px;
  background: var(--warning);
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 0 8px rgba(255, 165, 2, 0.6);
}

.timeline-overflow-indicator {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: var(--space-2) var(--space-3);
  border-radius: 8px;
  border: 2px solid var(--primary);
  font-size: var(--font-size-xs);
  color: var(--neutral-700);
  cursor: help;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.timeline-overflow-indicator:hover {
  transform: translateY(-50%) scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.timeline-overflow-indicator i {
  color: var(--primary);
}

.overflow-count {
  font-weight: var(--font-weight-bold);
  color: var(--primary);
  font-size: var(--font-size-sm);
}

.timeline-actions {
  margin-top: var(--space-4);
  display: flex;
  justify-content: center;
}

.timeline-toggle-btn {
  background: white;
  border: 2px solid var(--primary);
  color: var(--primary);
  padding: var(--space-2) var(--space-4);
  border-radius: 8px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.timeline-toggle-btn:hover {
  background: var(--primary);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.timeline-toggle-btn.active {
  background: linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 80%, black) 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.timeline-sample-info {
  color: var(--primary);
  font-weight: var(--font-weight-medium);
}

.timeline-dot-container {
  position: absolute;
  top: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateY(-50%);
}

.dot-date-label {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  font-size: var(--font-size-xs);
  color: var(--neutral-700);
  font-weight: var(--font-weight-semibold);
  margin-top: var(--space-3);
  text-align: center;
  white-space: nowrap;
  background: white;
  padding: var(--space-2) var(--space-3);
  border-radius: 8px;
  border: 2px solid var(--neutral-200);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.timeline-dot:hover + .dot-date-label,
.dot-date-label:hover {
  border-color: var(--primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateX(-50%) translateY(-2px);
}

@media (max-width: 768px) {
  .intervenant-header {
    grid-template-columns: auto 1fr;
    gap: var(--space-3);
  }

  .budget-metrics {
    grid-template-columns: 1fr;
    gap: var(--space-2);
  }

  .progress-timeline-row {
    grid-template-columns: 1fr;
    gap: var(--space-3);
  }

  .timeline-track {
    height: 60px;
    padding: var(--space-4) 0;
  }

  .timeline-dot {
    width: 12px;
    height: 12px;
  }

  .timeline-dot.important-transaction {
    width: 16px;
    height: 16px;
  }

  .dot-date-label {
    font-size: 10px;
    margin-top: var(--space-3);
    padding: var(--space-1) var(--space-2);
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .timeline-overflow-indicator {
    position: relative;
    right: auto;
    top: auto;
    transform: none;
    margin-top: var(--space-3);
    align-self: flex-end;
  }

  .intervenant-item {
    padding: var(--space-4);
  }

  .metric {
    padding: var(--space-3);
  }

  .metric-icon {
    width: 36px;
    height: 36px;
  }

  .intervenant-name {
    font-size: var(--font-size-lg);
  }

  .contact-item {
    font-size: var(--font-size-xs);
  }
}

/* All Transactions Modal Styles */
.all-transactions-modal {
  padding: var(--space-4);
}

.modal-header-info {
  margin-bottom: var(--space-4);
  padding-bottom: var(--space-3);
  border-bottom: 2px solid var(--neutral-200);
}

.modal-header-info h4 {
  margin: 0 0 var(--space-2) 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--neutral-900);
}

.transaction-count {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--neutral-600);
  font-weight: var(--font-weight-semibold);
}

.transactions-table {
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
}

.transactions-table .table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.transactions-table .table thead {
  background: linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 80%, black) 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.transactions-table .table th {
  padding: var(--space-3);
  text-align: left;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.transactions-table .table tbody tr {
  border-bottom: 1px solid var(--neutral-200);
  transition: all 0.2s ease;
}

.transactions-table .table tbody tr:hover {
  background: rgba(102, 126, 234, 0.05);
  transform: translateX(2px);
}

.transactions-table .table tbody tr.important-row {
  background: rgba(255, 71, 87, 0.05);
  border-left: 4px solid var(--error);
}

.transactions-table .table tbody tr.important-row:hover {
  background: rgba(255, 71, 87, 0.1);
}

.transactions-table .table td {
  padding: var(--space-3);
  color: var(--neutral-700);
}

.amount-cell {
  font-weight: var(--font-weight-semibold);
  color: var(--neutral-900);
}

.amount-cell .largest-amount {
  color: var(--error);
  font-weight: var(--font-weight-bold);
  padding: var(--space-1) var(--space-2);
  background: rgba(255, 71, 87, 0.1);
  border-radius: 4px;
}
</style>
